import { FC, useState, useRef, useCallback } from "react"
import { User } from "firebase/auth"
import {
	UploadCloud,
	X,
	Loader2,
	CheckCircle2,
	AlertCircle,
	File,
	Folder,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { useAuth } from "@/hooks/useAuth"
import { useUpload, UploadTask } from "@/contexts/UploadContext"

interface FileUploadProps {
	caseId: string
	multiple?: boolean
	className?: string
	accept?: string
	onFilesSelected?: (files: File[]) => void
	addToQueueImmediately?: boolean
}

export const FileUpload: FC<FileUploadProps> = ({
	caseId,
	multiple = true,
	className,
	accept = "*",
	onFilesSelected,
	addToQueueImmediately = true,
}) => {
	const [isDragging, setIsDragging] = useState(false)
	const [showPickerDialog, setShowPickerDialog] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const folderInputRef = useRef<HTMLInputElement>(null)
	const { user } = useAuth()
	const { dispatchUploadAction, uploadState } = useUpload()

	const handleNewFiles = useCallback(
		(newFiles: File[], isFolder: boolean = false) => {
			// Filter out system files and empty folders
			let filesToProcess = newFiles.filter(file => {
				// Skip system files
				if (file.name.startsWith('.') || file.name === 'Thumbs.db') {
					return false
				}
				// Skip empty files (folders show as 0 bytes)
				if (file.size === 0 && !file.type) {
					return false
				}
				return true
			})

			// Warn if large folder
			if (isFolder && filesToProcess.length > 100) {
				if (!confirm(`This folder contains ${filesToProcess.length} files. Do you want to continue?`)) {
					if (fileInputRef.current) fileInputRef.current.value = ""
					return
				}
			}

			// If not multiple, only take first file
			if (!multiple && !isFolder) {
				filesToProcess = filesToProcess.slice(0, 1)
			}

			if (onFilesSelected) {
				onFilesSelected(filesToProcess)
			}

			if (addToQueueImmediately) {
				if (!user) {
					console.error("Cannot add files to queue: User not logged in.")
					return
				}
				if (!caseId) {
					console.error("Cannot add files to queue: caseId is missing.")
					return
				}

				const newTasksToAdd: Omit<
					UploadTask,
					"status" | "progress" | "uploadedBytes" | "id"
				>[] = filesToProcess.map((file) => {
					// Get relative path for folder uploads
					const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || ''
					const fileName = relativePath || file.name

					return {
						fileName: fileName,
						fileSize: file.size,
						fileType: file.type,
						fileLastModified: file.lastModified,
						caseId: caseId,
					}
				})

				newTasksToAdd.forEach((taskPayload, index) => {
					dispatchUploadAction({
						type: "ADD_TASK",
						payload: { task: taskPayload, file: filesToProcess[index] },
					})
				})
			}

			if (fileInputRef.current) {
				fileInputRef.current.value = ""
			}
			if (folderInputRef.current) {
				folderInputRef.current.value = ""
			}
		},
		[
			multiple,
			onFilesSelected,
			addToQueueImmediately,
			caseId,
			dispatchUploadAction,
			user,
		]
	)


	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(true)
	}

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(false)
	}

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(false)
		if (e.dataTransfer.files) {
			handleNewFiles(Array.from(e.dataTransfer.files), false)
		}
	}

	const handleBrowse = () => {
		setShowPickerDialog(true)
	}

	const handleSelectFiles = () => {
		setShowPickerDialog(false)
		fileInputRef.current?.click()
	}

	const handleSelectFolder = () => {
		setShowPickerDialog(false)
		folderInputRef.current?.click()
	}

	const hasActiveLocalInstanceUploads = false

	return (
		<div className={className}>
			<div
				className={cn(
					"border-2 border-dashed rounded-lg p-6 transition-all duration-200 ease-in-out text-center",
					isDragging
						? "border-primary bg-primary/5"
						: "border-border hover:border-primary/50"
				)}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				aria-label="File upload drop zone"
			>
				<input
					ref={fileInputRef}
					type="file"
					accept={accept}
					multiple={multiple}
					onChange={(e) => {
						if (e.target.files) {
							handleNewFiles(Array.from(e.target.files), false)
						}
					}}
					className="hidden"
					aria-describedby="file-upload-instructions"
				/>
				<input
					ref={folderInputRef}
					type="file"
					// @ts-expect-error - webkitdirectory is not in React types but works in browsers
					webkitdirectory=""
					multiple
					onChange={(e) => {
						if (e.target.files) {
							handleNewFiles(Array.from(e.target.files), true)
						}
					}}
					className="hidden"
					aria-describedby="file-upload-instructions"
				/>

				<div
					className="flex flex-col items-center justify-center space-y-3"
					id="file-upload-instructions"
				>
					<div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary animate-float">
						<UploadCloud className="h-6 w-6" />
					</div>
					<div className="space-y-1">
						<p className="text-sm font-medium">Drag and drop your files or folders here</p>
						<p className="text-xs text-muted-foreground">or</p>
						<Button
							type="button"
							variant="secondary"
							size="sm"
							className="mt-2"
							onClick={handleBrowse}
							aria-label="Browse for files or folders to upload"
						>
							Browse Files & Folders
						</Button>
					</div>
					<p className="text-xs text-muted-foreground">
						Supports{" "}
						{accept === "*"
							? "all file types"
							: accept
									.split(",")
									.map((s) => s.trim())
									.join(", ")}
					</p>
				</div>
			</div>

			<Dialog open={showPickerDialog} onOpenChange={setShowPickerDialog}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>What would you like to upload?</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-2 pt-4">
						<Button
							variant="outline"
							className="w-full justify-start"
							onClick={handleSelectFiles}
						>
							<File className="mr-2 h-4 w-4" />
							Select Files
						</Button>
						<Button
							variant="outline"
							className="w-full justify-start"
							onClick={handleSelectFolder}
						>
							<Folder className="mr-2 h-4 w-4" />
							Select Folder
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}
